import { FormNameEnum, OrganizationDetailFragment } from '@alice-financial/alice-sdk'
import { Alert, Link, Typography } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { Navigate } from 'react-router'
import { PageBody } from '../../../routes/PageBody'
import { useCurrentOrgRootPath, useOrg } from '../../organization/useOrg'
import { ConnectPaymentMethodStep } from '../ConnectPaymentMethodStep'
import { AliceCardProgramSetupInfo } from './AliceCardProgramSetupInfo'
import { UnifiedFundingForm } from './UnifiedFundingForm'
import { makeOrgFileUploadInputDisplayConfig } from '../../../survey/questionTypes/FileUploadInputDisplay'
import { useOrgId } from '../../useOrgId'
import { useOrgFormStatus } from '../../survey/useOrgFormFields'
import { FormInputProps } from '../../../survey/types'

export const FORM_NAME = FormNameEnum.SecureBanking
export const FORM_VERSION = 1

const baseConfig = {
  questionId: 'secureBankingFile',
  required: false,
} as const
const SECURE_FILE_UPLOAD = makeOrgFileUploadInputDisplayConfig({
  questionText: 'Payroll banking accounts file',
  questionSubtext: 'Ensure location names, account numbers, and routing numbers are clearly labeled.',
  ...baseConfig,
})
const SecureFileUpload = SECURE_FILE_UPLOAD.Input
const SECURE_FILE_UPLOAD_POST_UPLOAD = makeOrgFileUploadInputDisplayConfig({
  questionText: 'Payroll banking accounts file (processing)',
  questionSubtext: 'We will enter the payroll banking information you have supplied within 2 business days',
  ...baseConfig,
})
const SecureFileUploadPostUpload = SECURE_FILE_UPLOAD_POST_UPLOAD.Input
const QUESTIONS = [SECURE_FILE_UPLOAD] as const

const UploadBillingAccountsInputs = ({ formName, formVersion }: FormInputProps) => {
  const orgId = useOrgId()
  const formStatus = useOrgFormStatus(orgId, FORM_NAME, FORM_VERSION)
  return formStatus === 'complete' ? (
    <SecureFileUploadPostUpload formName={formName} formVersion={formVersion} />
  ) : (
    <SecureFileUpload formName={formName} formVersion={formVersion} />
  )
}

const uploadBankingAccounts = {
  Page: UploadBillingAccountsInputs,
  questions: QUESTIONS,
}
export const PAGES = [uploadBankingAccounts]

export const AddPayGroupsAlert = ({ org }: { org: OrganizationDetailFragment }) => {
  const currentOrgRootPath = useCurrentOrgRootPath()
  if (!org || org.aliceCardProgram?.hasUnifiedBanking || org.isOnboarded) return null
  const question = org.employers.length === 0 ? 'Enter your first pay group' : 'Do you have more pay groups?'
  return (
    <Alert severity="info" sx={{ p: 1 }}>
      <Typography variant="body2">
        <strong>{question}</strong>
        <br />
        You can create new pay groups and assign them to locations from the{' '}
        <Link to={`${currentOrgRootPath}/locations`}>Locations page</Link>.
      </Typography>
    </Alert>
  )
}

export const FundingAccountSetupPage = () => {
  const org = useOrg()
  const currentOrgRootPath = useCurrentOrgRootPath()
  if (!org) return null
  const fundingConnected = org.aliceCardProgram.hasFundingConnected
  if (fundingConnected) return <Navigate to={`${currentOrgRootPath}/alice-card-program`} replace />

  return (
    <PageBody maxWidth="md">
      <Typography variant="h1" gutterBottom>
        Contributions Account setup
      </Typography>
      <AliceCardProgramSetupInfo />
      <UnifiedFundingForm org={org} />
      <ConnectPaymentMethodStep org={org} />
      <AddPayGroupsAlert org={org} />
    </PageBody>
  )
}
